import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import FirstVisitMonitorLink from "@/components/feature/FirstVisitMonitorLink";
import { ConcernList, FeatureList, TreatmentIndexNav } from "@/pages/treatment/components/TreatmentSections";
import { DowntimeTable, FlowSteps, MethodTable } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentCta, TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import TreatmentPriceLink from "@/pages/treatment/components/TreatmentPriceLink";
import EyeSubHero from "./EyeSubHero";
import type { EyeDetail } from "@/mocks/eyePages";
import { isPendingValue } from "@/utils/isPendingContent";

/**
 * 二重・目元カテゴリ詳細ページ（二重埋没法）の正式コンテンツページ。
 * NOTE: 点数・糸の本数・糸の商品名・固定方法・保証内容・施術時間・麻酔方法・ダウンタイム日数・
 *       抜糸の有無・持続期間・料金などは未確認のため作成しない。未確定の項目は「—」を維持する。
 */

type SectionTone = "plain" | "taupe";

function Section({
  id,
  en,
  jp,
  lead,
  tone = "plain",
  children,
}: {
  id: string;
  en: string;
  jp: string;
  lead?: string;
  tone?: SectionTone;
  children: ReactNode;
}) {
  const toneClass = tone === "taupe" ? "bg-secondary-50" : "bg-background-50";
  return (
    <section id={id} className={`${toneClass} py-16 md:py-20 lg:py-24`}>
      <div className="u-container">
        <Reveal>
          <div className="flex flex-col gap-5 md:max-w-2xl">
            <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">{en}</span>
            <span className="block h-px w-10 bg-accent-500" />
            <h2 className="font-heading text-[24px] leading-[1.55] tracking-[0.01em] text-foreground-950 md:text-[32px]">
              {jp}
            </h2>
            {lead ? (
              <p className="whitespace-pre-line text-[13px] leading-[2.1] text-foreground-700 md:text-sm">{lead}</p>
            ) : null}
          </div>
        </Reveal>
        <div className="mt-10 md:mt-14">{children}</div>
      </div>
    </section>
  );
}

/** ページ下部の控えめな関連リンク。大きなカードは使わず、細い罫線＋テキストリンク＋小さな矢印。 */
function RelatedLinks({ items }: { items: { label: string; to: string }[] }) {
  return (
    <section className="border-t border-background-300/70 bg-background-50 py-16 md:py-20 lg:py-24">
      <div className="u-container">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1fr_1.8fr] md:gap-16 lg:gap-24">
          <Reveal>
            <div className="flex flex-col">
              <span className="en-eyebrow text-[10px] text-secondary-700 md:text-[11px]">RELATED LINKS</span>
              <span className="mt-5 block h-px w-10 bg-accent-500" />
              <h2 className="mt-6 font-heading text-[20px] leading-[1.6] text-foreground-950 md:text-[24px]">
                関連ページ
              </h2>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <ul className="border-t border-foreground-950/12">
              {items.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="group flex items-center justify-between gap-4 border-b border-foreground-950/12 py-6 transition-colors"
                  >
                    <span className="font-heading text-[15px] text-foreground-900 transition-colors group-hover:text-accent-700 md:text-base">
                      {link.label}
                    </span>
                    <i
                      className="ri-arrow-right-line text-base text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-accent-700"
                      aria-hidden
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function EyeSubDetail({ data }: { data: EyeDetail }) {
  // ダウンタイムがすべて未確定の場合はセクションごと非表示にする
  const downtimeRows = data.downtime.filter((item) => !isPendingValue(item.value));
  const indexItems = [
    { label: "この施術について", target: "sp-about" },
    { label: "このようなお悩みの方へ", target: "sp-recommended" },
    { label: "特徴", target: "sp-features" },
    { label: "施術情報", target: "sp-method" },
    { label: "施術の流れ", target: "sp-flow" },
    ...(downtimeRows.length > 0 ? [{ label: "ダウンタイム", target: "sp-downtime" }] : []),
    { label: "リスク・注意事項", target: "sp-risk" },
    { label: "料金", target: "sp-price" },
    { label: "Q&A", target: "sp-faq" },
  ];

  return (
    <>
      <EyeSubHero data={data} />
      <TreatmentIndexNav items={indexItems} />

      {/* ABOUT */}
      <Section id="sp-about" en="ABOUT TREATMENT" jp={data.aboutJp} tone="plain">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            {data.aboutParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </Reveal>
      </Section>

      {/* RECOMMENDED FOR */}
      <Section id="sp-recommended" en="RECOMMENDED FOR" jp={data.recommendedJp} tone="taupe">
        <ConcernList items={data.recommended} />
      </Section>

      {/* FEATURES */}
      <Section id="sp-features" en="FEATURES" jp={data.featuresJp} tone="plain">
        <FeatureList items={data.features} />
      </Section>

      {/* METHOD */}
      <Section id="sp-method" en="METHOD" jp="施術情報" tone="taupe">
        <Reveal>
          <MethodTable items={data.method} />
        </Reveal>
      </Section>

      {/* FLOW */}
      <Section id="sp-flow" en="TREATMENT FLOW" jp="施術の流れ" tone="plain">
        <FlowSteps items={data.flow} />
      </Section>

      {/* DOWNTIME（未確定項目は非表示。全項目が未確定の場合はセクションごと非表示） */}
      {downtimeRows.length > 0 ? (
        <Section id="sp-downtime" en="DOWNTIME" jp={data.downtimeJp} tone="taupe">
          <DowntimeTable items={downtimeRows} />
        </Section>
      ) : null}

      {/* RISK / PRECAUTIONS */}
      <Section id="sp-risk" en="RISK / PRECAUTIONS" jp={data.riskJp} tone="plain">
        <Reveal>
          <div className="max-w-3xl border-l-2 border-accent-500 pl-5 md:pl-6">
            <div className="space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
              {data.riskParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </Section>

      {/* PRICE */}
      <Section id="sp-price" en="PRICE" jp="料金" tone="taupe">
        <div className="max-w-3xl">
          <div className="grid grid-cols-[1.6fr_1fr] border-t border-foreground-950/20 pb-3 pt-4 text-[11px] tracking-[0.15em] text-foreground-500">
            <span>対象</span>
            <span className="text-right">料金</span>
          </div>
          {data.price.map((row) => (
            <div
              key={row.label}
              className="grid grid-cols-[1.6fr_1fr] items-baseline border-t border-foreground-950/12 py-5"
            >
              <span className="pr-6 font-heading text-[15px] text-foreground-950">{row.label}</span>
              <span className="text-right font-label text-[15px] text-foreground-900">{row.price}</span>
            </div>
          ))}
          <p className="mt-4 text-[12px] leading-[2] text-foreground-500">表示価格はすべて税込です。</p>
        </div>
        <div className="mt-8">
          <TreatmentPriceLink categoryId={data.priceTab} categoryJp="二重・目元" label="二重・目元の料金を見る" />
        </div>
        {/* 二重埋没法 モニターLP導線（医療脱毛と同様、「料金表を見る」の直後に配置。モニター価格は公式サイトに掲載しない） */}
        {data.slug === "double-eyelid" ? (
          <div className="mt-6 md:mt-8">
            <FirstVisitMonitorLink
              title="初めての方限定・二重埋没法モニターのご案内"
              description="初めてご来院いただく方向けの二重埋没法モニターの内容・条件は、専用ページでご案内しています。"
              label="二重埋没法モニターを見る"
              url="https://sbscl.biz/futae/"
            />
          </div>
        ) : null}
      </Section>

      {/* Q&A */}
      <Section id="sp-faq" en="Q&amp;A" jp="よくあるご質問" tone="plain">
        <div className="max-w-3xl">
          <TreatmentFaq items={data.faqs} />
        </div>
      </Section>

      {/* RELATED LINKS */}
      <RelatedLinks items={data.related} />

      {/* FINAL CTA */}
      <TreatmentCta lineLabel={data.ctaLineLabel} treatmentJp={data.jp} />
    </>
  );
}